import React from "react";
import { useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import RichBody from "@/components/RichBody";
import { usePage } from "@/lib/usePage";
import { AGENCY } from "@/config/agency";

const ROUTE_TO_KEY = {
  "mentions-legales": "legal_mentions",
  "cgv": "legal_cgv",
  "confidentialite": "legal_confidentialite",
  "cookies": "legal_cookies",
};

// Corps par défaut (repli) pour les pages en texte libre.
const CGV_BODY = `## Objet
Les présentes CGV régissent la vente de prestations d'expéditions organisées par HorsSignal. Toute candidature ou réservation vaut prise de connaissance et acceptation sans réserve des présentes.

## Prix
Les prix sont indiqués en euros (€), par personne. Sauf mention contraire écrite sur la fiche de l'expédition, sont INCLUS : l'encadrement et les guides locaux, l'hébergement prévu, les transferts sur place et les repas mentionnés. Sont EXCLUS : les vols internationaux, l'assurance voyage, les visas, les vaccins, les dépenses personnelles et tout service non expressément listé.

## Paiement
Un acompte de 30% du prix total est exigé à la réservation. Le solde est dû au plus tard 60 jours avant le départ. À défaut de paiement du solde dans les délais, la réservation peut être annulée.

## Annulation par le client
En cas d'annulation par le client, un barème de remboursement dégressif s'applique selon le délai avant départ, conformément au droit français.

## Assurance voyage obligatoire
Une assurance voyage couvrant au minimum le rapatriement, les frais médicaux et l'annulation est obligatoire et à la charge du client.

## Acceptation des risques
Le client reconnaît les risques inhérents à une expédition extrême et accepte un dégagement de responsabilité de HorsSignal, sauf faute grave ou manquement caractérisé de l'agence.

## Droit applicable & litiges
Les présentes sont soumises au droit français. En cas de litige, à défaut de résolution amiable, le client peut recourir à la médiation de la consommation.`;

const CONF_BODY = `## Responsable du traitement
${AGENCY.operator}, ${AGENCY.address} — ${AGENCY.email}.

## Données collectées
Nom, prénom, email, téléphone, et selon le formulaire utilisé : message, expédition concernée, informations nécessaires à la réservation. Les informations de paiement sont traitées directement par notre prestataire de paiement.

## Finalités
Répondre à vos demandes, traiter vos réservations, vous adresser notre newsletter si vous y avez consenti, et respecter nos obligations légales.

## Durée de conservation
3 ans pour les demandes non suivies de réservation ; durée de la relation contractuelle puis obligations comptables pour les réservations ; jusqu'à désinscription pour la newsletter.

## Vos droits
Droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité, exerçables à ${AGENCY.email}. Droit de réclamation auprès de la CNIL (www.cnil.fr).`;

const COOKIES_BODY = `## Cookies techniques essentiels
Nécessaires au fonctionnement du site. Consentement non requis.

## Cookies de mesure d'audience
Statistiques de fréquentation. Déposés uniquement avec votre consentement.

## Cookies réseaux sociaux
Intégration Instagram / TikTok. Déposés uniquement avec votre consentement.

## Gestion de vos préférences
Vous pouvez accepter, refuser ou personnaliser les cookies non essentiels via le bandeau affiché lors de votre première visite.

## Contact
${AGENCY.email}`;

const FALLBACKS = {
  legal_mentions: {
    title: "Mentions légales", seo_description: "Mentions légales — HORS SIGNAL.",
    operator: AGENCY.operator, legal_form: AGENCY.legalForm, siren: AGENCY.siren, siret: AGENCY.siret,
    tva: AGENCY.tva, address: AGENCY.address, email: AGENCY.email, phone: AGENCY.phone, director: AGENCY.director,
    atout_france: AGENCY.atoutFrance, guarantee: AGENCY.guarantee, rc_pro: AGENCY.rcPro,
    host: AGENCY.host, host_address: AGENCY.hostAddress,
    ip_text: "L'ensemble des contenus du site (textes, images, photographies, logos) est protégé par le droit de la propriété intellectuelle. Toute reproduction sans autorisation préalable est interdite.",
    law_text: "Les présentes mentions légales sont soumises au droit français. En cas de litige, les tribunaux français sont seuls compétents.",
  },
  legal_cgv: { title: "Conditions Générales de Vente", seo_description: "CGV — HORS SIGNAL.", body: CGV_BODY },
  legal_confidentialite: { title: "Politique de confidentialité", seo_description: "Politique de confidentialité — HORS SIGNAL.", body: CONF_BODY },
  legal_cookies: { title: "Politique de cookies", seo_description: "Politique de cookies — HORS SIGNAL.", body: COOKIES_BODY },
};

// Une info par ligne : label mono discret + valeur lisible.
function DefRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3">
      {label ? (
        <dt className="font-mono-hs text-[10px] uppercase tracking-widest text-[#8a8a80] sm:w-52 shrink-0 pt-1.5">{label}</dt>
      ) : null}
      <dd className="text-[15px] sm:text-base text-[#D8D8D0] leading-relaxed">{value}</dd>
    </div>
  );
}

function MentionsSections({ c }) {
  const sections = [
    { h: "Éditeur du site", items: [
      ["", c.operator],
      ["Statut juridique", c.legal_form],
      ["SIREN", c.siren],
      ["SIRET", c.siret],
      ["N° TVA intracommunautaire", c.tva],
      ["Siège social", c.address],
      ["Email", c.email],
      ["Téléphone", c.phone],
    ] },
    { h: "Directeur de la publication", items: [["", c.director]] },
    { h: "Immatriculation — activité de voyage", items: [
      ["Numéro Atout France", c.atout_france],
      ["Garantie financière", c.guarantee],
      ["Assurance RC professionnelle", c.rc_pro],
    ] },
    { h: "Hébergement du site", items: [
      ["Hébergeur", c.host],
      ["Adresse", c.host_address],
    ] },
    { h: "Propriété intellectuelle", text: c.ip_text },
    { h: "Droit applicable", text: c.law_text },
    { h: "Contact", items: [["Email", c.email]] },
  ];
  return (
    <div className="mt-14 space-y-9">
      {sections.map((s, i) => (
        <div
          key={i}
          data-testid={`legal-section-${i + 1}`}
          className={`flex flex-col gap-4 sm:flex-row sm:gap-8 ${i > 0 ? "pt-7 border-t border-white/[0.07]" : ""}`}
        >
          <div className="flex items-baseline gap-4 sm:w-64 shrink-0">
            <span className="font-mono-hs text-[11px] text-[#BC5D2E] tracking-[0.2em] tabular-nums pt-1">
              {String(i + 1).padStart(2, "0")}
            </span>
            <h2 className="font-clash text-[22px] sm:text-2xl tracking-tight text-[#F5F5F0] leading-snug">{s.h}</h2>
          </div>
          <div className="flex-1 sm:pt-0.5">
            {s.text ? (
              <p className="text-[15px] sm:text-base text-[#B5B5AD] leading-[1.85]">{s.text}</p>
            ) : (
              <dl className="space-y-2.5">
                {s.items.map(([label, val], j) => (
                  <DefRow key={j} label={label} value={val} />
                ))}
              </dl>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LegalPage() {
  const { pathname } = useLocation();
  const doc = pathname.replace("/", "");
  const cmsKey = ROUTE_TO_KEY[doc] || "legal_mentions";
  const fallback = FALLBACKS[cmsKey];
  const { content: c } = usePage(cmsKey, fallback);
  const title = c.title || fallback.title;

  return (
    <div className="pt-32 pb-24">
      <Seo path={`/${doc}`} title={`${title} — HORS SIGNAL`} description={c.seo_description || fallback.seo_description} />
      <div className="mx-auto max-w-3xl px-6 sm:px-12">
        <header className="border-b border-white/10 pb-8">
          <h1 className="font-clash font-semibold text-4xl sm:text-5xl tracking-tight text-[#F5F5F0]" data-testid="legal-title">{title}</h1>
          <div className="mt-5 h-px w-16 bg-[#BC5D2E]" />
        </header>
        {cmsKey === "legal_mentions" ? <MentionsSections c={c} /> : <RichBody text={c.body} />}
      </div>
    </div>
  );
}
